import { useCallback, useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import ProductGridSkeleton from './ProductGridSkeleton';
import GrillaDeCarpetas from './moodboards/GrillaDeCarpetas';
import VistaDeCarpeta from './moodboards/VistaDeCarpeta';
import {
  cambiarColorDeMoodboard,
  crearMoodboard,
  eliminarFoto,
  eliminarMoodboard,
  fetchFotos,
  fetchMoodboards,
  renombrarMoodboard,
  subirFoto,
  type Moodboard,
  type MoodboardFoto,
} from '../pages/v2/moodboardsApi';

/**
 * Tab "Moodboards" de la pantalla Mi marca —
 * ADR-2026-09-13-moodboards-carpetas-de-referencia.
 *
 * Carpetas donde la marca junta fotos de referencia. Este componente es el
 * ÚNICO que habla con `moodboardsApi`: la grilla y la vista de adentro sólo
 * dibujan y avisan, así el estado de las carpetas y el de las fotos no se
 * pueden ir por caminos distintos.
 *
 * Sólo lo ve el rubro Moda e Indumentaria (`rubroVeMoodboards`); el gate está en
 * `MiMarca.tsx`, junto al resto de los tabs, y no acá adentro: quién ve qué es
 * una decisión de la pantalla, no de su contenido.
 */
export const MiMarcaMoodboardsTab = ({
  companyId,
  brandId,
  /**
   * `false` mientras `useBrandProfile` no resolvió. Sin esto la primera carga
   * saldría con `brandId` en `null`, que para `fetchMoodboards` significa "todas
   * las marcas" y no "todavía no sé" — el mismo cuidado que el tab de productos
   * (ADR-2026-08-04-los-productos-esperan-a-saber-de-que-marca-son).
   */
  marcaResuelta,
}: {
  companyId: string | null;
  brandId: string | null;
  marcaResuelta: boolean;
}) => {
  const [carpetas, setCarpetas] = useState<Moodboard[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [abierta, setAbierta] = useState<Moodboard | null>(null);
  const [fotos, setFotos] = useState<MoodboardFoto[]>([]);
  const [cargandoFotos, setCargandoFotos] = useState(false);
  const [subiendo, setSubiendo] = useState(0);

  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    let cancelado = false;
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelado) setUserId(data.user?.id ?? null);
    });
    return () => {
      cancelado = true;
    };
  }, []);

  const avisar = (e: unknown, quePasaba: string) => {
    console.error(`[MiMarcaMoodboardsTab] ${quePasaba}`, e);
    setError(
      e instanceof Error ? e.message : `No pudimos ${quePasaba}. Probá de nuevo.`,
    );
  };

  // Refresco SIN skeleton, para después de crear/borrar/subir: la grilla se
  // actualiza en el lugar en vez de parpadear entera.
  const recargarCarpetas = useCallback(async () => {
    if (!companyId) return;
    try {
      setCarpetas(await fetchMoodboards({ companyId, brandId }));
    } catch (e) {
      avisar(e, 'cargar las carpetas');
    }
  }, [companyId, brandId]);

  useEffect(() => {
    if (!companyId || !marcaResuelta) return;
    let cancelado = false;
    setCargando(true);
    fetchMoodboards({ companyId, brandId })
      .then((lista) => {
        if (!cancelado) setCarpetas(lista);
      })
      .catch((e) => {
        if (!cancelado) avisar(e, 'cargar las carpetas');
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [companyId, brandId, marcaResuelta]);

  const abrir = async (carpeta: Moodboard) => {
    if (!companyId) return;
    setAbierta(carpeta);
    setFotos([]);
    setCargandoFotos(true);
    try {
      setFotos(await fetchFotos({ companyId, moodboardId: carpeta.id }));
    } catch (e) {
      avisar(e, 'cargar las fotos');
    } finally {
      setCargandoFotos(false);
    }
  };

  const volver = () => {
    setAbierta(null);
    setFotos([]);
    // La portada y el contador de la carpeta que se acaba de mirar pueden haber
    // cambiado (se subieron o borraron fotos): se piden de nuevo al salir, que es
    // justo cuando la grilla vuelve a estar en pantalla.
    void recargarCarpetas();
  };

  const agregarFotos = async (files: File[]) => {
    if (!companyId || !abierta) return;
    setError(null);
    setSubiendo((n) => n + files.length);
    // De a una y no todas juntas: cada foto que termina entra a la grilla en el
    // acto, así una tanda de veinte muestra avance en vez de quedarse quieta
    // hasta el final. El contador que baja es lo que dibuja los tiles en espera.
    for (const file of files) {
      try {
        const foto = await subirFoto({
          companyId,
          moodboardId: abierta.id,
          file,
        });
        setFotos((previas) => [foto, ...previas]);
      } catch (e) {
        avisar(e, 'subir la foto');
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
  };

  if (!companyId || cargando) {
    return (
      <ProductGridSkeleton
        count={6}
        gridClassName="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 xl:grid-cols-4"
        tileAspectClassName="aspect-[4/3]"
        tileClassName="rounded-[12px]"
        withLabel
      />
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 flex items-center gap-2.5 rounded-md border border-red-200 bg-red-50 p-3 text-[14px] text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <p className="min-w-0 flex-1">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="shrink-0 text-[13px] font-medium underline"
          >
            Cerrar
          </button>
        </div>
      )}

      {abierta ? (
        <VistaDeCarpeta
          carpeta={abierta}
          fotos={fotos}
          cargando={cargandoFotos}
          subiendo={subiendo}
          onVolver={volver}
          onAgregar={agregarFotos}
          onEliminarFoto={async (foto) => {
            try {
              await eliminarFoto({ fotoId: foto.id, path: foto.path });
              setFotos((previas) => previas.filter((f) => f.id !== foto.id));
            } catch (e) {
              avisar(e, 'eliminar la foto');
            }
          }}
        />
      ) : (
        // Acá iba un párrafo explicando para qué sirven las carpetas. Se movió ADENTRO
        // de la carpeta (ADR-2026-09-13-moodboards-color-icono-cerrado-y-solo-escritorio):
        // la explicación es sobre QUÉ cargar y para qué, y eso importa cuando el usuario
        // ya entró a cargar, no cuando está mirando la grilla.
        <GrillaDeCarpetas
          carpetas={carpetas}
          onAbrir={abrir}
          onCrear={async (nombre) => {
            try {
              const carpeta = await crearMoodboard({
                companyId,
                brandId,
                nombre,
                userId,
              });
              setCarpetas((previas) => [carpeta, ...previas]);
            } catch (e) {
              avisar(e, 'crear la carpeta');
            }
          }}
          onRenombrar={async (id, nombre) => {
            // Optimista: el nombre se cambia en pantalla y después se guarda. Es
            // un texto corto y sin efectos, y esperar al servidor para ver la
            // letra nueva se siente roto.
            const antes = carpetas;
            setCarpetas((previas) =>
              previas.map((c) => (c.id === id ? { ...c, nombre } : c)),
            );
            try {
              await renombrarMoodboard(id, nombre);
            } catch (e) {
              setCarpetas(antes);
              avisar(e, 'guardar el nombre');
            }
          }}
          onCambiarColor={async (id, color) => {
            // Optimista por el mismo motivo que el nombre, y acá pesa más: el color
            // es un cambio VISUAL y verlo un segundo después del click se lee como
            // que el click no funcionó.
            const antes = carpetas;
            setCarpetas((previas) =>
              previas.map((c) => (c.id === id ? { ...c, color } : c)),
            );
            try {
              await cambiarColorDeMoodboard(id, color);
            } catch (e) {
              setCarpetas(antes);
              avisar(e, 'guardar el color');
            }
          }}
          onEliminar={async (id) => {
            try {
              await eliminarMoodboard({ companyId, moodboardId: id });
              setCarpetas((previas) => previas.filter((c) => c.id !== id));
            } catch (e) {
              avisar(e, 'eliminar la carpeta');
            }
          }}
        />
      )}
    </div>
  );
};

export default MiMarcaMoodboardsTab;
